import mapboxgl from 'mapbox-gl/dist/mapbox-gl-dev';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import {
  findThirdPoint,
  calculateAngleBetweenPoints,
  calculateCoordinates
} from '../../utils/geometry';

mapboxgl.accessToken =
  'pk.eyJ1IjoiZGFuaWw4MTIiLCJhIjoiY2xua2pvaTBwMXZ6NTJtcnU2a3loYXg4ayJ9.3a5iflXEtUqDuUHf6Zm_vA';

function base64ToFile(base64String, filename) {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  const blob = new Blob([u8arr], { type: mime });

  return new File([blob], filename, { type: mime });
}

class Point {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }

  isZero() {
    return this.x === 0 && this.y === 0;
  }
}

class Transform {
  constructor() {
    this.rotation = 0;
    this._offset = new Point(0, 0);
  }

  set offset(value) {
    this._offset = value;
  }

  get offset() {
    return this._offset;
  }
}

const satellite = 'mapbox://styles/mapbox/satellite-v9';
// const streets = 'mapbox://styles/mapbox/streets-v12';

class MapController {
  constructor({ ref, plan = {} }) {
    this.center = [-122.108915, 37.370093]; // [lng, lat]
    this.zoom = 18;
    this.dragPan = true;
    this.transform = new Transform();

    this.map = new mapboxgl.Map({
      container: ref.current,
      style: satellite,
      center: this.center,
      zoom: this.zoom,
      dragPan: this.dragPan
    });

    this.plan = plan;
    this._handlers = [];

    this.onLoad = () => null;

    this.enableGeocoder = this.enableGeocoder.bind(this);
    this.addGeocoderListener = this.addGeocoderListener.bind(this);
    this.removeGeocoderListener = this.removeGeocoderListener.bind(this);
    this.drawPlan = this.drawPlan.bind(this);
    this.setPlan = this.setPlan.bind(this);

    this.onMove = this.onMove.bind(this);
    this.onMoveRotation = this.onMoveRotation.bind(this);
    this.onUpRotation = this.onUpRotation.bind(this);
    this.onUp = this.onUp.bind(this);

    this.update = this.update.bind(this);
    this.createRotateControl = this.createRotateControl.bind(this);

    this.onChange = this.onChange.bind(this);
    this.setHandler = this.setHandler.bind(this);
    this.takeScreenshot = this.takeScreenshot.bind(this);
  }

  takeScreenshot() {
    // this.loading = true;
    return new Promise((resolve, reject) => {
      this.map.flyTo({
        center: this.center,
        zoom: 19,
        essential: false
      });
      this.map.once('idle', () => {
        // this.loading = false;
        resolve(
          base64ToFile(
            this.map.getCanvas().toDataURL('image/jpeg', 0.95),
            `map-snapshot-${Date.now()}`
          )
        );
      });
      this.map.once('moveend', () => {
        this.map.triggerRepaint();
      });
    });
  }

  enableGeocoder() {
    this.geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      language: 'en',
      zoom: 19,
      placeholder: 'YOUR ADDRESS'
    });
    this.map.addControl(this.geocoder, 'top-left');

    this.geocoder.on('result', ({ result }) => {
      try {
        const { center } = result;
        this.center = center;
        this.address = result.place_name;
        if (this.plan) this.drawPlan();
        this.onChange();
      } catch (error) {}
    });

    this.geocoder.on('result', ({ result }) => {
      try {
        const { context, text } = result;
        let state, county, city;

        if (result.place_type && result.place_type.includes('place')) {
          city = text;
        }

        if (context && Array.isArray(context)) {
          context.forEach(c => {
            if (c.id.startsWith('region')) {
              state = c.text;
            } else if (c.id.startsWith('district')) {
              county = c.text;
            } else if (c.id.startsWith('place') && !city) {
              city = c.text;
            }
          });
        }

        console.log('City:', city);
        console.log('State:', state);
        console.log('County:', county);
        this.city = city;
        this.state = state;
        this.county = county;
        this.onChange();
      } catch (error) {
        console.error(error);
      }
    });
  }

  update() {
    const { coordinates } = calculateCoordinates(
      this.center[0],
      this.center[1],
      this.plan.meta.length + 8, // 4 for all side margin
      this.plan.meta.width + 8,
      this.transform.rotation
    );

    const centerRotation = findThirdPoint(this.center, coordinates[1], 0.00001);

    const coordinatesRotate = calculateCoordinates(
      centerRotation[0],
      centerRotation[1],
      6,
      6,
      this.transform.rotation
    );

    const planData = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[...coordinates, coordinates[0]]]
      }
    };

    const controlData = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[...coordinatesRotate.coordinates, coordinatesRotate.coordinates[0]]]
      }
    };

    const planSource = this.map.getSource('plan');
    const svgSource = this.map.getSource('svg-source');
    const rotateSource = this.map.getSource('rotate-source');
    const rotateControlSource = this.map.getSource('rotate-control');

    planSource.setData(planData);
    svgSource.setCoordinates(coordinates);

    rotateSource.setCoordinates(coordinatesRotate.coordinates);
    rotateControlSource.setData(controlData);
  }

  addGeocoderListener(eventType, handler) {
    this.geocoder.on(eventType, handler);
  }

  removeGeocoderListener(eventType, handler) {
    this.geocoder.off(eventType, handler);
  }

  onMove(e) {
    const coords = e.lngLat;

    this.map.getCanvas().style.cursor = 'grabbing';

    if (this.transform.offset.isZero())
      this.transform.offset = new Point(this.center[0] - coords.lng, this.center[1] - coords.lat);

    this.center = [coords.lng + this.transform.offset.x, coords.lat + this.transform.offset.y];
    this.update();
  }

  onMoveRotation(e) {
    const coords = e.lngLat;
    const defaultCoords = calculateCoordinates(
      this.center[0],
      this.center[1],
      this.plan.meta.length + 8,
      this.plan.meta.width + 8,
      0
    );
    const zeroCoords = [
      this.center[0] + defaultCoords.lngDiff,
      this.center[1] + defaultCoords.latDiff
    ];
    const angle = calculateAngleBetweenPoints(zeroCoords, this.center, [coords.lng, coords.lat]);

    this.transform.rotation = angle * -1;

    this.update();
  }

  onUpRotation(e) {
    this.map.getCanvas().style.cursor = '';
    this.map.off('mousemove', this.onMoveRotation);
    this.map.off('touchmove', this.onMoveRotation);
    this.onChange();
  }

  onUp(e) {
    this.map.getCanvas().style.cursor = '';
    this.transform.offset.x = 0;
    this.transform.offset.y = 0;
    this.map.off('mousemove', this.onMove);
    this.map.off('touchmove', this.onMove);
    this.onChange();
  }

  onChange() {
    for (const handler of this._handlers) {
      handler(this.data);
    }
  }

  setHandler(handler) {
    this._handlers.push(handler);
  }

  setPlan(plan) {
    this.plan = plan;
    this.drawPlan();
  }

  clear() {
    if (this.map.getLayer('svg-layer')) this.map.removeLayer('svg-layer');
    if (this.map.getLayer('plan-bounding')) this.map.removeLayer('plan-bounding');
    if (this.map.getLayer('plan')) this.map.removeLayer('plan');
    if (this.map.getLayer('rotate-layer')) this.map.removeLayer('rotate-layer');
    if (this.map.getLayer('rotate-control')) this.map.removeLayer('rotate-control');

    if (this.map.getSource('plan')) this.map.removeSource('plan');
    if (this.map.getSource('svg-source')) this.map.removeSource('svg-source');
    if (this.map.getSource('rotate-source')) this.map.removeSource('rotate-source');
    if (this.map.getSource('rotate-control')) this.map.removeSource('rotate-control');
  }

  clearRotateLayer() {
    if (this.map.getLayer('rotate-layer')) this.map.removeLayer('rotate-layer');
    if (this.map.getLayer('rotate-control')) this.map.removeLayer('rotate-control');
    if (this.map.getSource('rotate-source')) this.map.removeSource('rotate-source');
    if (this.map.getSource('rotate-control')) this.map.removeSource('rotate-control');
  }

  createRotateLayer() {
    const { coordinates } = calculateCoordinates(
      this.center[0],
      this.center[1],
      this.plan.meta.length + 8,
      this.plan.meta.width + 8,
      this.transform.rotation
    );
    this.createRotateControl(coordinates[1]);
  }

  drawPlan() {
    this.clear();

    if (!this.plan || !this.plan.planId) return;

    this.loading = true;

    fetch(`https://3d.homewip.com/svg/${this.plan.planId}/`)
      .then(response => response.text())
      .then(svgData => {
        const img = new Image();
        const svg = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svg);

        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);

          const imageData = canvas.toDataURL('image/png');

          const { coordinates } = calculateCoordinates(
            this.center[0],
            this.center[1],
            this.plan.meta.length + 8,
            this.plan.meta.width + 8,
            this.transform.rotation
          );

          this.createPlanLayer(coordinates, imageData);
          this.createRotateControl(coordinates[1]);

          this.onChange();

          URL.revokeObjectURL(url);
        };

        img.src = url;
      })
      .catch(error => console.error('Error loading SVG:', error))
      .finally(() => {
        this.loading = false;
      });
  }

  createPlanLayer(coordinates, imageData) {
    this.map.addSource('svg-source', {
      type: 'image',
      url: imageData,
      coordinates: coordinates
    });

    this.map.addSource('plan', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[...coordinates, coordinates[0]]]
        }
      }
    });

    this.map.addLayer({
      id: 'plan',
      type: 'fill',
      source: 'plan',
      layout: {},
      paint: {
        'fill-color': '#4bc484',
        'fill-opacity': 0.3
      }
    });

    this.map.addLayer({
      id: 'plan-bounding',
      type: 'line',
      source: 'plan',
      layout: {},
      paint: {
        'line-color': '#000',
        'line-width': 1
      }
    });

    this.map.addLayer({
      id: 'svg-layer',
      type: 'raster',
      source: 'svg-source'
    });

    this.map.on('mousedown', 'plan', e => {
      e.preventDefault();

      this.map.getCanvas().style.cursor = 'grabbing';
      this.map.on('mousemove', this.onMove);
      this.map.once('mouseup', this.onUp);
    });

    this.map.on('touchstart', 'plan', e => {
      if (e.points.length !== 1) return;

      e.preventDefault();

      this.map.on('touchmove', this.onMove);
      this.map.once('touchend', this.onUp);
    });

    this.map.on('mouseup', 'plan', e => {
      this.map.getCanvas().style.cursor = '';
    });

    this.map.on('mouseenter', 'plan', e => {
      this.map.getCanvas().style.cursor = 'move';
    });

    this.map.on('mouseleave', 'plan', e => {
      this.map.getCanvas().style.cursor = '';
    });
  }

  createRotateControl(coordinates) {
    const centerRotation = findThirdPoint(this.center, coordinates, 0.00001);
    const coordinatesRotate = calculateCoordinates(
      centerRotation[0],
      centerRotation[1],
      6,
      6,
      this.transform.rotation
    );

    this.map.addSource('rotate-source', {
      type: 'image',
      url: '/public/icons/rotation_icon.png',
      coordinates: coordinatesRotate.coordinates
    });

    this.map.addSource('rotate-control', {
      type: 'geojson',
      data: {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [[...coordinatesRotate.coordinates, coordinatesRotate.coordinates[0]]]
        }
      }
    });

    this.map.addLayer({
      id: 'rotate-control',
      type: 'fill',
      source: 'rotate-control',
      layout: {},
      paint: {
        'fill-color': '#4bc484',
        'fill-opacity': 0.0
      }
    });

    this.map.addLayer({
      id: 'rotate-layer',
      type: 'raster',
      source: 'rotate-source'
    });

    this.map.on('mousedown', 'rotate-control', e => {
      e.preventDefault();

      this.map.on('mousemove', this.onMoveRotation);
      this.map.once('mouseup', this.onUpRotation);
    });

    this.map.on('touchstart', 'rotate-control', e => {
      if (e.points.length !== 1) return;

      e.preventDefault();

      this.map.on('touchmove', this.onMoveRotation);
      this.map.once('touchend', this.onUpRotation);
    });
  }

  get data() {
    return {
      center: this.center,
      rotation: this.transform.rotation,
      address: this.address || '',
      state: this.state || '',
      county: this.county || '',
      city: this.city || ''
    };
  }

  set loading(value) {
    this.onLoad(value);
    this._loading = value;
  }

  get loading() {
    return this._loading;
  }
}

export default MapController;
